import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck } from "lucide-react";

/**
 * One-shot privacy reassurance toast that appears on the first time
 * a user opens the AR mirror in their browser/install, then quietly
 * never shows again (localStorage flag).
 *
 * Lives ABOVE the camera feed for ~4 seconds. Drag-mom voice keeps
 * it warm; Apple App Review reads the actual content for the camera
 * permission audit so it spells out "frames stay on this device."
 */
const STORAGE_KEY = "trubeaute.mirror_privacy_toast_shown";
const DISMISS_AFTER_MS = 4500;

const PrivacyToast = ({ ephemeral = false }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let seen = false;
    try {
      if (ephemeral) throw new Error("ephemeral-demo");
      seen = window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      // localStorage blocked (private mode, etc.) — still show once
      // per session, never marks as seen.
    }
    if (seen) return;
    setVisible(true);
    const t = setTimeout(() => {
      setVisible(false);
      try {
        if (ephemeral) return;
        window.localStorage.setItem(STORAGE_KEY, "1");
      } catch {
        /* silently skip */
      }
    }, DISMISS_AFTER_MS);
    return () => clearTimeout(t);
  }, [ephemeral]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="absolute top-20 left-1/2 -translate-x-1/2 z-30 max-w-[88%] sm:max-w-md pointer-events-none"
          data-testid="mirror-privacy-toast"
        >
          <div className="bg-black/70 backdrop-blur-md border border-white/15 rounded-2xl px-4 py-3 flex items-start gap-3 shadow-xl">
            <ShieldCheck className="h-4 w-4 text-[#86EFAC] shrink-0 mt-0.5" />
            <p className="text-white/90 text-xs leading-relaxed">
              Live frames stay on this device.
              <span className="text-white/55"> Trouvé only sees a still when you tap
              "Generate routine."</span>
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PrivacyToast;
